import { useEffect, useRef, useState } from 'react';

import { useInView } from 'react-intersection-observer';

import {
  PatientEnrollmentStatus,
  usePatientEnrollmentStatus
} from '@/hooks/usePatientEnrollmentStatus';

import { ColumnDef, ColumnId, DEFAULT_COLUMNS } from './useColumnPreferences';

/** Columns whose cells read the per-row CPE checklist evaluation. */
export const CHECKLIST_COLUMN_IDS: ColumnId[] = [
  'setup',
  'diagnosis',
  'following',
  'demographics',
  'insurance'
];

/** Hidden unless an internal user turns Enrollment mode on. */
export const ENROLLMENT_MODE_COLUMN_IDS: ColumnId[] = ['setup', 'demographics', 'insurance'];

/**
 * Enrollment mode shows Setup, Demographics, and Insurance. Diagnosis and Following stay
 * optional columns any user can turn on, and enrollment mode shows those too.
 */
export function columnsForEnrollmentMode(
  columns: ColumnDef[],
  enrollmentMode: boolean
): ColumnDef[] {
  if (!enrollmentMode) {
    return columns.filter((column) => !ENROLLMENT_MODE_COLUMN_IDS.includes(column.id));
  }
  const shown = new Set(columns.map((column) => column.id));
  return DEFAULT_COLUMNS.filter(
    (column) => CHECKLIST_COLUMN_IDS.includes(column.id) || shown.has(column.id)
  ).map((column) => ({
    ...(columns.find((current) => current.id === column.id) ?? column),
    visible: true
  }));
}

export interface RowChecklist extends PatientEnrollmentStatus {
  /** The checklist queries are running (or done) for this row. */
  enabled: boolean;
}

/** How many rows may run the checklist queries at once. Clinic settings are cached after the first. */
const MAX_CONCURRENT_CHECKS = 3;
let runningChecks = 0;
const checkQueue: Array<() => void> = [];

function takeCheckSlot(): Promise<void> {
  if (runningChecks < MAX_CONCURRENT_CHECKS) {
    runningChecks += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    checkQueue.push(() => {
      runningChecks += 1;
      resolve();
    });
  });
}

function freeCheckSlot() {
  if (runningChecks > 0) runningChecks -= 1;
  const next = checkQueue.shift();
  if (next) next();
}

/**
 * One checklist evaluation per patient row, shared by every checklist column (Setup,
 * Diagnosis, Following, Demographics, Insurance). The list row paints first. Once that row
 * is on screen, the checklist calls start (a few at a time). Attach `ref` to the row.
 */
export function useRowChecklist(
  patientId: string,
  _status: string,
  active: boolean
): { ref: (node?: Element | null) => void; checklist: RowChecklist } {
  const { ref, inView } = useInView({ triggerOnce: true, skip: !active });
  const enabled = active && inView;
  const [started, setStarted] = useState(false);
  const holding = useRef(false);

  useEffect(() => {
    if (!enabled || started) return;
    let cancel = false;
    void takeCheckSlot().then(() => {
      if (cancel) {
        freeCheckSlot();
        return;
      }
      holding.current = true;
      setStarted(true);
    });
    return () => {
      cancel = true;
    };
  }, [enabled, started]);

  const { loading, error, evaluation } = usePatientEnrollmentStatus(patientId, enabled && started);

  useEffect(() => {
    if (!holding.current || loading) return;
    holding.current = false;
    freeCheckSlot();
  }, [loading, started]);

  useEffect(
    () => () => {
      if (!holding.current) return;
      holding.current = false;
      freeCheckSlot();
    },
    []
  );

  return {
    ref,
    checklist: {
      // Waiting for a slot is still loading. Otherwise the cell flashes "Couldn't load"
      // before the queries are allowed to start.
      loading: enabled && (!started || loading),
      error,
      evaluation,
      enabled
    }
  };
}
